//Signup

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import Input from '../common/Input';
import Button from '../common/Button';
import AnimalSelector from '../animals/AnimalSelector';
import { authService } from '../../services/auth';
import { setError } from '../../store/slices/authSlice';
import { RootState } from '../../store';
import { SignupForm as SignupFormType } from '../../types';
import {
  validateUsername,
  validatePassword,
  validateEmail,
  validateName,
  validateInitials,
  validateClassCode,
} from '../../utils/validators';
import { checkClassCodeExists, checkUsernameExists } from '../../services/firestore';

interface SignupFormProps {
  onBack: () => void;
}

type SignupStep = 'account' | 'personal' | 'animal';

const SignupForm: React.FC<SignupFormProps> = ({ onBack }) => {
  const dispatch = useDispatch();
  const { animals } = useSelector((state: RootState) => state.game);
  
  const [currentStep, setCurrentStep] = useState<SignupStep>('account');
  const [formData, setFormData] = useState<SignupFormType>({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    initials: '',
    email: '',
    classCode: '',
    selectedAnimalId: '',
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof SignupFormType, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState<string>('');

  const handleInputChange = (field: keyof SignupFormType, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    //Clear error if/when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    if (generalError) setGeneralError('');
  };

  const validateStep = (step: SignupStep): boolean => {
    const newErrors: Partial<Record<keyof SignupFormType, string>> = {};

    if (step === 'account') {
      const usernameValidation = validateUsername(formData.username);
      if (!usernameValidation.isValid) {
        newErrors.username = usernameValidation.errors[0];
      }

      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = passwordValidation.errors[0];
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      const classCodeValidation = validateClassCode(formData.classCode);
      if (!classCodeValidation.isValid) {
        newErrors.classCode = classCodeValidation.errors[0];
      }
    }

    if (step === 'personal') {
      const firstNameValidation = validateName(formData.firstName, 'First name');
      if (!firstNameValidation.isValid) {
        newErrors.firstName = firstNameValidation.errors[0];
      }

      const lastNameValidation = validateName(formData.lastName, 'Last name');
      if (!lastNameValidation.isValid) {
        newErrors.lastName = lastNameValidation.errors[0];
      }

      const initialsValidation = validateInitials(formData.initials);
      if (!initialsValidation.isValid) {
        newErrors.initials = initialsValidation.errors[0];
      }

      if (formData.email) {
        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) {
          newErrors.email = emailValidation.errors[0];
        }
      }
    }

    if (step === 'animal') {
      if (!formData.selectedAnimalId) {
        newErrors.selectedAnimalId = 'Please select an animal';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



const handleNext = async () => {
  if (validateStep(currentStep)) {
    //Validate class code with server
    if (currentStep === 'account') {
      setIsLoading(true);
      try {
        const usernameExists = await checkUsernameExists(formData.username);
        if (usernameExists) {
          setErrors(prev => ({ ...prev, username: 'Username already exists' }));
          setIsLoading(false);
          return;
        }

        const normalizedClassCode = formData.classCode.trim().toUpperCase();

        const classCodeExists = await checkClassCodeExists(normalizedClassCode);
        if (!classCodeExists) {
          setErrors(prev => ({ ...prev, classCode: 'Invalid class code' }));
          setIsLoading(false);
          return;
        }

        setFormData(prev => ({ ...prev, classCode: normalizedClassCode }));
        
      } catch (error) {
        setErrors(prev => ({ ...prev, classCode: 'Error validating information' }));
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }
    
    switch (currentStep) {
      case 'account':
        setCurrentStep('personal');
        break;
      case 'personal':
        setCurrentStep('animal');
        break;
      case 'animal':
        handleSubmit();
        break;
    }
  }
};

  const handleSubmit = async () => {
    setIsLoading(true);
    setGeneralError('');

    try {
      await authService.signUp(formData);
      //Redictect on auth state change
    } catch (error: any) {
      const errorMessage = error.message || 'An error occurred during signup';
      setGeneralError(errorMessage);
      dispatch(setError(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-powder-200 max-w-lg mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-powder-800 mb-2">
          Join the Forest!
        </h2>
        <p className="text-powder-600">
          Create your account to start growing
        </p>
        
        {/* Progress indicator */}
        <div className="flex justify-center mt-6 space-x-2">
          {['account', 'personal', 'animal'].map((step, index) => (
            <div
              key={step}
              className={`w-8 h-2 rounded-full transition-colors ${
                step === currentStep
                  ? 'bg-sage-500'
                  : ['account', 'personal', 'animal'].indexOf(currentStep) > index
                  ? 'bg-sage-300'
                  : 'bg-powder-200'
              }`}
            />
          ))}
        </div>
      </div>

      {generalError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
        >
          <p className="text-red-700 text-sm">{generalError}</p>
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        {currentStep === 'account' && (
          <motion.div
            key="account"
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <Input
              label="Username"
              type="text"
              value={formData.username}
              onChange={(value) => handleInputChange('username', value)}
              placeholder="Choose a unique username"
              required
              disabled={isLoading}
              error={errors.username}
              maxLength={20}
            />

            <Input
              label="Password"
              type="password"
              value={formData.password}
              onChange={(value) => handleInputChange('password', value)}
              placeholder="Create a strong password"
              required
              disabled={isLoading}
              error={errors.password}
            />

            <Input
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(value) => handleInputChange('confirmPassword', value)}
              placeholder="Confirm your password"
              required
              disabled={isLoading}
              error={errors.confirmPassword}
            />

            <Input
              label="Class Code"
              type="text"
              value={formData.classCode}
              onChange={(value) => handleInputChange('classCode', value)}
              placeholder="Enter your class code"
              required
              disabled={isLoading}
              error={errors.classCode}
            />
          </motion.div>
        )}

        {currentStep === 'personal' && (
          <motion.div
            key="personal"
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <Input
              label="First Name"
              type="text"
              value={formData.firstName}
              onChange={(value) => handleInputChange('firstName', value)}
              placeholder="Your first name"
              required
              disabled={isLoading}
              error={errors.firstName}
              maxLength={50}
            />

            <Input
              label="Last Name"
              type="text"
              value={formData.lastName}
              onChange={(value) => handleInputChange('lastName', value)}
              placeholder="Your last name"
              required
              disabled={isLoading}
              error={errors.lastName}
              maxLength={50}
            />

            <Input
              label="Initials"
              type="text"
              value={formData.initials}
              onChange={(value) => handleInputChange('initials', value.toUpperCase())}
              placeholder="Your initials (e.g., JS)"
              required
              disabled={isLoading}
              error={errors.initials}
              maxLength={4}
            />

            <Input
              label="Email (Optional)"
              type="email"
              value={formData.email || ''}
              onChange={(value) => handleInputChange('email', value)}
              placeholder="your.email@example.com"
              disabled={isLoading}
              error={errors.email}
            />
          </motion.div>
        )}

        {currentStep === 'animal' && (
          <motion.div
            key="animal"
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-powder-800 mb-2">
                Choose Your Forest Guide
              </h3>
              <p className="text-powder-600 text-sm">
                Select an animal that represents you in the forest
              </p>
            </div>

            <AnimalSelector
              animals={animals}
              selectedAnimalId={formData.selectedAnimalId}
              onSelect={(animalId) => handleInputChange('selectedAnimalId', animalId)}
              error={errors.selectedAnimalId}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 space-y-4">
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleNext}
          disabled={isLoading}
        >
          {isLoading 
            ? 'Creating Account...' 
            : currentStep === 'animal' 
            ? 'Create Account' 
            : 'Next'
          }
        </Button>

        <div className="flex space-x-4">
          {currentStep !== 'account' && (
            <Button
              type="button"
              variant="outline"
              size="md"
              fullWidth
              onClick={() => {
                switch (currentStep) {
                  case 'personal':
                    setCurrentStep('account');
                    break;
                  case 'animal':
                    setCurrentStep('personal');
                    break;
                }
              }}
              disabled={isLoading}
            >
              Previous
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            size="md"
            fullWidth
            onClick={onBack}
            disabled={isLoading}
          >
            Back to Login
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
